import React, { useContext, useMemo, useState, useRef } from 'react';
import { PageContext } from '../PageContext';
import { Page, UserProfile, ChatConversation } from '../types';
import { useAuth } from '../AuthContext';
import { useChat } from '../ChatContext';
import { isUserOnline } from '../utils';
import NewMessageModal from '../components/NewMessageModal';
import ConfirmationModal from '../components/ConfirmationModal';

interface ConversationItemProps {
    conversation: ChatConversation;
    onClick: () => void;
    onLongPress: () => void;
    otherUser: UserProfile | undefined;
    unreadCount: number;
}

const ConversationItem: React.FC<ConversationItemProps> = ({ conversation, onClick, onLongPress, otherUser, unreadCount }) => {
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handlePressStart = (e: React.TouchEvent | React.MouseEvent) => {
        // Only start timer for left click or touch
        if (e.type === 'mousedown' && (e as React.MouseEvent).button !== 0) return;

        longPressTimer.current = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            onLongPress();
            longPressTimer.current = null;
        }, 500); // 500ms for long press
    };

    const handlePressEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    if (!otherUser) {
        return null; // Don't render if the other user's profile isn't found
    }

    const displayName = otherUser.displayName;
    const photoURL = otherUser.photoURL;
    
    // Safely handle missing lastMessage for new conversations
    const timeString = conversation.lastMessage?.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '';
    const messagePreview = conversation.lastMessage?.text || (conversation.lastMessage?.imageUrl ? 'Sent an image' : 'Start a conversation');

    return (
        <div
            onClick={onClick}
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onContextMenu={(e) => e.preventDefault()} // Prevent context menu on long press
            className="flex items-center p-3 space-x-4 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 transition-colors select-none"
        >
            <div className="relative flex-shrink-0">
                <img src={photoURL} alt={displayName} className="w-14 h-14 rounded-full object-cover" />
                {unreadCount > 0 && (
                     <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 border-2 border-white text-white text-[10px] flex items-center justify-center font-bold">
                        {unreadCount}
                    </span>
                )}
                {isUserOnline(otherUser) && (
                    <span className="absolute bottom-1 right-1 block h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                    <p className="font-bold truncate text-gray-800">{displayName}</p>
                    <p className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {timeString}
                    </p>
                </div>
                <p className={`text-sm truncate ${unreadCount > 0 ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
                    {messagePreview}
                </p>
            </div>
        </div>
    );
};


const ChatListPage: React.FC = () => {
    const pageContext = useContext(PageContext);
    const { userProfile, allUsers } = useAuth();
    const { conversations, messages, deleteConversation } = useChat();
    const [isNewMessageModalOpen, setNewMessageModalOpen] = useState(false);
    
    // Deletion State
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState<ChatConversation | null>(null);

    const sortedConversations = useMemo(() => {
        return [...conversations].sort((a, b) => {
            const timeA = a.lastMessage?.createdAt?.toMillis() || 0;
            const timeB = b.lastMessage?.createdAt?.toMillis() || 0;
            return timeB - timeA;
        });
    }, [conversations]);

    const handleConversationClick = (recipient: UserProfile) => {
        pageContext?.switchPage(Page.ChatConversation, recipient.displayName, { recipient });
    };
    
    const handleNewMessage = () => {
      setNewMessageModalOpen(true);
    }

    const handleLongPress = (conversation: ChatConversation) => {
        setConversationToDelete(conversation);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (conversationToDelete) {
            await deleteConversation(conversationToDelete.id);
            setDeleteModalOpen(false);
            setConversationToDelete(null);
        }
    };

    if (!userProfile) return null;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                    <span className="aurora-text">Messages</span>
                </h2>
                <button 
                  onClick={handleNewMessage} 
                  className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-2"
                >
                    <i className="ph-duotone ph-pencil-simple-line"></i> New
                </button>
            </div>

            <div className="space-y-2">
                {sortedConversations.length > 0 ? (
                    sortedConversations.map(convo => {
                        const otherUserId = convo.participants.find(p => p !== userProfile.uid);
                        const otherUser = allUsers.find(u => u.uid === otherUserId);
                        const unreadCount = (messages[convo.id] || []).filter(m => !m.read && m.senderId !== userProfile.uid).length;

                        return (
                            <ConversationItem
                                key={convo.id}
                                conversation={convo}
                                onClick={() => otherUser && handleConversationClick(otherUser)}
                                onLongPress={() => handleLongPress(convo)}
                                otherUser={otherUser}
                                unreadCount={unreadCount}
                            />
                        );
                    })
                ) : (
                    <p className="text-center text-gray-500 py-12">No conversations yet. Start a new one!</p>
                )}
            </div>
            
            <NewMessageModal 
              isOpen={isNewMessageModalOpen}
              onClose={() => setNewMessageModalOpen(false)}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Conversation?"
                message="Are you sure you want to delete this conversation? This action cannot be undone."
                confirmText="Delete"
                triggerRect={null}
            />
        </div>
    );
};

export default ChatListPage;